import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatMarkdown } from "@/components/formatMarkdown";
import { Template } from "@/types/template";
import { ExternalLink, MarketingLink } from "@/components/OptionalLink";
import { Badge } from "@/components/ui/badge";
import { ProviderType } from "@/types";
import { useRouter } from "next/router";
import { capitalize } from "@/lib/utils";

export const getTagLabel = (tag: string) => {
  const remap = {
    oss: "OSS",
    nvidia: "NVIDIA",
    ai: "AI",
    openai: "OpenAI",
    "e-learning": "E-Learning",
    "e-commerce": "E-Commerce",
    crm: "CRM",
    cms: "CMS",
    erp: "ERP",
    baas: "BaaS",
    saas: "SaaS",
    paas: "PaaS",
    iaas: "IaaS",
    devops: "DevOps",
    gdpr: "GDPR",
    email: "E-mail",
    rsvp: "RSVP",
    rss: "RSS",
    mcp: "MCP",
    vpn: "VPN",
    ide: "IDE",
    nextjs: "Next.js",
    faq: "FAQ",
    "ai-agent": "AI Agent"
  } as Record<string, string>;

  if (remap[tag]) {
    return remap[tag];
  }

  if (tag.indexOf("-") !== -1) {
    return tag.split("-").map(capitalize).join(" ");
  }

  return capitalize(tag);
};

export const getTemplateSourceLabel = (source?: string) => {
  if (!source) {
    return "";
  }

  if (source.startsWith("file:")) {
    return "Local";
  }

  if (source === "https://github.com/dollardeploy/templates") {
    return "Official";
  }

  if (source.startsWith("user://")) {
    return "User";
  }

  return "Community";
};

/** Builds the link to the template source, pointing at the sub path when present. */
const getSourceCodeUrl = (app: Template["app"]) => {
  if (!app.sourcePath) {
    return app.repositoryUrl;
  }

  const base = app.repositoryUrl.endsWith("/") ? app.repositoryUrl : app.repositoryUrl + "/";
  const path = app.sourcePath.startsWith("/") ? app.sourcePath.slice(1) : app.sourcePath;
  return `${base}tree/${app.sourceBranch ?? "main"}/${path}`;
};

export const ListTemplateCard = ({ template }: { template: Template }) => {
  const router = useRouter();
  const provider = router.query.provider as ProviderType | undefined;
  const detailHref = `/r/${template.id}${provider ? `?provider=${provider}` : ""}`;
  const tags = template.tags?.toSorted() ?? [];

  return (
    // The whole card is clickable: the title link is stretched over the card via
    // `after:absolute after:inset-0`. Tags and footer actions sit above it with `z-10`.
    <Card className="group relative flex h-full w-full flex-col gap-3 py-5 transition-[transform,box-shadow,border-color] duration-200 hover:border-primary/50 hover:shadow-lg motion-safe:hover:-translate-y-0.5">
      <CardHeader className="justify-items-center px-5 text-center">
        <img
          src={template.logo}
          alt={`${template.name} logo`}
          loading="lazy"
          className="mb-1.5 h-10 rounded-md object-contain"
        />
        <CardTitle className="text-lg tracking-tight">
          <MarketingLink
            href={detailHref}
            className={`after:absolute after:inset-0 plausible-event-name=DeployTemplate plausible-event-template=${template.id}`}
          >
            {template.name}
          </MarketingLink>
        </CardTitle>
        <CardDescription className="relative z-10 flex flex-wrap justify-center gap-2 pt-1.5">
          {template.experimental && <Badge variant="destructive">Experimental</Badge>}
          {tags.slice(0, 2).map(tag => (
            <Badge
              key={tag}
              variant="outline"
              className="hover:bg-primary hover:text-primary-foreground"
              asChild
            >
              <MarketingLink href={`/r?tag=${tag}`}>{getTagLabel(tag)}</MarketingLink>
            </Badge>
          ))}
          {tags.length > 2 && (
            <Badge variant="outline" asChild>
              <MarketingLink
                href={detailHref}
                className={`plausible-event-name=DeployTemplate plausible-event-template=${template.id}`}
              >
                +{tags.length - 2}
              </MarketingLink>
            </Badge>
          )}
        </CardDescription>
      </CardHeader>

      <CardContent className="px-5">
        <div className="line-clamp-3 text-sm leading-relaxed text-secondary-foreground">
          {formatMarkdown(template.intro)}
        </div>
      </CardContent>

      <CardFooter className="relative z-10 mt-auto flex justify-between gap-2 px-5">
        <Button asChild size="sm">
          <MarketingLink
            href={detailHref}
            className={`plausible-event-name=DeployTemplate plausible-event-template=${template.id}`}
          >
            Deploy
          </MarketingLink>
        </Button>
        {template.app?.repositoryUrl && (
          <Button asChild size="sm" variant="secondary">
            <ExternalLink href={getSourceCodeUrl(template.app)}>Source code</ExternalLink>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
